import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Typography, Box, Button, LinearProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import WebcamCard, { WebcamCardHandle } from '../components/WebcamCard';
import SignalsPanel from '../components/SignalsPanel';
import HealthCard from '../components/HealthCard';
import { useStatsSettings } from '../context/Context';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../config/api';
import { getModelCapabilities } from '../utils/modelCapabilities';

const ema = (prev: number, next: number, alpha = 0.5) =>
  prev === 0 ? next : Math.round(alpha * next + (1 - alpha) * prev);

const VideoMonitorPage: React.FC = () => {
  const { healthData, updateHealthData, healthHistory, selectedStats, selectedModel } = useStatsSettings();
  const navigate = useNavigate();
  const smoothedRef = useRef({ hr: 0, sbp: 0, dbp: 0, map: 0, hrv: 0, rr: 0 });
  const sessionStartRef = useRef<Date | null>(null);
  const readingsRef = useRef<Array<{
    timestamp: string; hr: number; sbp: number; dbp: number; hrv: number; rr: number;
  }>>([]);

  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoName, setVideoName] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);

  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ppgSignal, setPpgSignal] = useState<number[]>([]);
  const [ecgSignal, setEcgSignal] = useState<number[]>([]);
  const [icgSignal, setIcgSignal] = useState<number[]>([]);
  const [bpSignal, setBpSignal] = useState<number[]>([]);
  const [bufferFrames, setBufferFrames] = useState<number | undefined>(undefined);
  const [isFirstResponseReceived, setIsFirstResponseReceived] = useState(false);
  const [mediapipeFailed, setMediapipeFailed] = useState(false);
  const [inferenceError, setInferenceError] = useState('');
  const [inferenceProgress, setInferenceProgress] = useState(0);
  const [framesCollected, setFramesCollected] = useState(0);
  const [framesRequired, setFramesRequired] = useState(0);

  const webcamCardRef = useRef<WebcamCardHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedModelLower = selectedModel.toLowerCase();
  const isDeepPhysModel = selectedModelLower.includes('deepphys');
  const isPhysFormerModel = selectedModelLower.includes('physformer');
  const isBPFormerModel = selectedModelLower.includes('bpformer');
  const isRhythmNetModel = selectedModelLower.includes('rhythmnet');
  const isSeeVitaModel = selectedModelLower.includes('seevita') || isDeepPhysModel || isPhysFormerModel || isBPFormerModel || isRhythmNetModel;
  const expectedInitialFrames = isBPFormerModel ? 300 : isRhythmNetModel ? 160 : isPhysFormerModel ? 160 : isDeepPhysModel ? 180 : isSeeVitaModel ? 180 : 50;
  const modelCapabilities = useMemo(() => getModelCapabilities(selectedModel), [selectedModel]);

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  const loadVideo = useCallback((file: File) => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(URL.createObjectURL(file));
    setVideoName(file.name);
    setIsMonitoring(false);
    setIsFirstResponseReceived(false);
    setInferenceError('');
    setInferenceProgress(0);
    setPpgSignal([]);
    setEcgSignal([]);
    setIcgSignal([]);
    setBpSignal([]);
    smoothedRef.current = { hr: 0, sbp: 0, dbp: 0, map: 0, hrv: 0, rr: 0 };
  }, [videoUrl]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadVideo(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('video/')) loadVideo(file);
  };

  const handleStartMonitoring = () => {
    if (!selectedModel) {
      setInferenceError('Choose a prediction model from the dashboard settings before starting.');
      return;
    }
    if (!videoUrl) {
      setInferenceError('Upload a video file first.');
      return;
    }
    if (!isMonitoring) {
      setIsLoading(true);
      setIsFirstResponseReceived(false);
      setInferenceError('');
      setInferenceProgress(0);
      setFramesCollected(0);
      setFramesRequired(expectedInitialFrames);
      sessionStartRef.current = new Date();
      readingsRef.current = [];
      setTimeout(() => {
        setIsMonitoring(true);
        setIsLoading(false);
        webcamCardRef.current?.toggleInference();
      }, 800);
    } else {
      const endedAt = new Date();
      const startedAt = sessionStartRef.current ?? endedAt;
      const readings = readingsRef.current;
      if (readings.length > 0) {
        apiFetch('/api/sessions', {
          method: 'POST',
          body: JSON.stringify({
            modelUsed: selectedModel,
            startedAt: startedAt.toISOString(),
            endedAt: endedAt.toISOString(),
            durationSeconds: Math.round((endedAt.getTime() - startedAt.getTime()) / 1000),
            readings,
          }),
        }).catch(() => {});
      }
      setIsMonitoring(false);
      setInferenceProgress(0);
      setFramesCollected(0);
      setFramesRequired(0);
      smoothedRef.current = { hr: 0, sbp: 0, dbp: 0, map: 0, hrv: 0, rr: 0 };
      webcamCardRef.current?.toggleInference();
    }
  };

  const handleApiResponse = (response: { SBP: number[] | null; DBP: number[] | null; PPG: number[]; HR: number; RR: number; HRV: number; ECG: number[]; ICG?: number[]; BP_wave?: number[]; MAP?: number | null; SpO2?: number | null; buffer_frames?: number }) => {
    if (mediapipeFailed) return;
    setIsFirstResponseReceived(true);
    setInferenceProgress(100);
    setInferenceError('');
    const s = smoothedRef.current;
    s.hr  = ema(s.hr,  Math.round(response.HR  || 0));
    // RhythmNet returns null BP until it has enough clean beats — don't pull the average toward 0
    const bpAvailable = response.SBP?.[0] != null && response.DBP?.[0] != null;
    if (bpAvailable) {
      s.sbp = ema(s.sbp, Math.round(response.SBP![0]));
      s.dbp = ema(s.dbp, Math.round(response.DBP![0]));
    }
    s.rr  = ema(s.rr,  Math.round(response.RR  || 0));
    s.hrv = ema(s.hrv, Math.round(response.HRV || 0));
    if (response.MAP != null) s.map = ema(s.map, Math.round(response.MAP));
    updateHealthData({
      ...(bpAvailable ? { systolicBP: s.sbp, diastolicBP: s.dbp } : {}),
      ...(response.MAP != null ? { map: s.map } : {}),
      ...(response.SpO2 != null ? { spo2: Math.round(response.SpO2) } : {}),
      heartRate: s.hr, respiratoryRate: s.rr, hrv: s.hrv,
    });
    readingsRef.current.push({
      timestamp: new Date().toISOString(),
      hr: s.hr, sbp: s.sbp, dbp: s.dbp, hrv: s.hrv, rr: s.rr,
    });
    if (response.PPG) setPpgSignal(response.PPG);
    if (response.ECG) setEcgSignal(response.ECG);
    if (response.ICG && response.ICG.length > 0) setIcgSignal(response.ICG);
    if (response.BP_wave && response.BP_wave.length > 0) setBpSignal(response.BP_wave);
    if (typeof response.buffer_frames === 'number') setBufferFrames(response.buffer_frames);
  };

  const handleApiError = (message: string) => {
    setInferenceError(message);
    setIsMonitoring(false);
    setIsLoading(false);
    setInferenceProgress(0);
    setFramesCollected(0);
    setFramesRequired(0);
  };

  const handleMediaPipeError = (failed: boolean) => {
    setMediapipeFailed(failed);
    if (failed) {
      setIsMonitoring(false);
      setIsLoading(false);
      setPpgSignal([]);
      setEcgSignal([]);
      setIcgSignal([]);
      setBpSignal([]);
      setInferenceProgress(0);
      setFramesCollected(0);
      setFramesRequired(0);
    }
  };

  const hasData = isMonitoring && isFirstResponseReceived;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)',
      px: { xs: 1.5, md: 3 },
      py: { xs: 1.5, md: 2.5 },
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 1.5, md: 2 },
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
        >
          Back
        </Button>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '4px', textTransform: 'uppercase' }}>
          SeeVita · Demo
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 80, justifyContent: 'flex-end' }}>
          {isMonitoring && !mediapipeFailed && (
            <>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: hasData ? '#4ade80' : '#fbbf24',
                boxShadow: hasData ? '0 0 6px #4ade80' : '0 0 6px #fbbf24',
                animation: 'pulse 1.8s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
              }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: hasData ? '#4ade80' : '#fbbf24' }}>
                {hasData ? 'Live' : 'Collecting…'}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {inferenceError && (
        <Alert severity="warning" sx={{ borderRadius: '12px', py: 0.5 }}>
          {inferenceError}
        </Alert>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
        gap: { xs: 1.5, md: 2 },
        flex: 1,
        minHeight: 0,
      }}>

        {/* Left column: video + upload + controls */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {videoUrl ? (
            <Box sx={{ ...cardSx, p: 0, overflow: 'hidden', position: 'relative' }}>
              <Box sx={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', borderRadius: '20px' }}>
                <WebcamCard
                  ref={webcamCardRef}
                  source="video"
                  videoSrc={videoUrl}
                  mode="api"
                  hideControls
                  onApiResponse={handleApiResponse}
                  onApiError={handleApiError}
                  onMediaPipeError={handleMediaPipeError}
                  onInferenceProgress={(progress, collected, required) => {
                    setInferenceProgress(progress);
                    setFramesCollected(collected);
                    setFramesRequired(required);
                  }}
                />
              </Box>
              {/* Overlay: video name + change button */}
              <Box sx={{
                position: 'absolute', bottom: 8, left: 8, right: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'rgba(0,0,0,0.55)', borderRadius: '10px', px: 1.2, py: 0.6,
              }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                  {videoName}
                </Typography>
                <Button
                  size="small"
                  onClick={() => { setVideoUrl(''); setVideoName(''); setIsMonitoring(false); }}
                  sx={{ color: '#60a5fa', textTransform: 'none', fontSize: '0.65rem', minWidth: 0, p: '2px 6px' }}
                >
                  Change
                </Button>
              </Box>
            </Box>
          ) : (
            /* Drop zone */
            <Box
              sx={{
                ...cardSx,
                aspectRatio: '1 / 1',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: isDragging ? '2px dashed #60a5fa' : '2px dashed rgba(255,255,255,0.12)',
                transition: 'border-color 0.2s',
                gap: 1,
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <UploadFileIcon sx={{ fontSize: 36, color: isDragging ? '#60a5fa' : 'rgba(255,255,255,0.25)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', textAlign: 'center' }}>
                Drop a video file here
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '0.7rem' }}>
                or click to browse
              </Typography>
            </Box>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            style={{ display: 'none' }}
            onChange={handleFilePick}
          />

          {/* Progress bar */}
          {isMonitoring && !isFirstResponseReceived && !mediapipeFailed && (
            <Box sx={{ ...cardSx, gap: 0.6, py: 1.2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' }}>
                  {isSeeVitaModel ? 'Collecting frames' : 'Preparing…'}
                </Typography>
                {isSeeVitaModel && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums' }}>
                    {framesCollected} / {framesRequired || expectedInitialFrames}
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={isSeeVitaModel ? inferenceProgress : 100}
                sx={{ height: 4, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.07)', '& .MuiLinearProgress-bar': { bgcolor: '#60a5fa', borderRadius: 999 } }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleStartMonitoring}
            disabled={isLoading || !selectedModel || !videoUrl}
            sx={{
              py: 1.4, borderRadius: '50px', fontSize: '0.88rem', fontWeight: 700, textTransform: 'none',
              bgcolor: isMonitoring ? 'transparent' : 'white',
              color: isMonitoring ? '#f87171' : '#0a1628',
              border: isMonitoring ? '1.5px solid rgba(248,113,113,0.45)' : 'none',
              boxShadow: isMonitoring ? 'none' : '0 4px 24px rgba(255,255,255,0.12)',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: isMonitoring ? 'rgba(239,68,68,0.08)' : '#e8f0fe', transform: 'translateY(-1px)' },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.18)', boxShadow: 'none', border: 'none' },
            }}
          >
            {isLoading ? 'Initializing…'
              : isMonitoring ? 'Stop'
              : !videoUrl ? 'Upload a video first'
              : !selectedModel ? 'Select a model first'
              : 'Run Inference'}
          </Button>
        </Box>

        {/* Right column: health stats + signals */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, minHeight: 0 }}>

          {mediapipeFailed ? (
            <Box sx={cardSx}>
              <Typography sx={{ color: '#f87171', fontSize: '0.85rem' }}>
                No face detected in the video. Make sure the video shows a face and try again.
              </Typography>
            </Box>
          ) : (
            <Box sx={cardSx}>
              <Typography sx={cardLabelSx}>Health Stats</Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                gridAutoRows: '1fr',
                gap: 1,
                opacity: hasData ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}>
                {selectedStats.heartRate && modelCapabilities.vitals.heartRate && <HealthCard title="Heart Rate" value={hasData ? healthData.heartRate : '—'} unit="BPM" bgColor="rgba(239,68,68,0.15)" textColor="#f87171" history={healthHistory.heartRate} />}
                {(selectedStats.systolicBP || selectedStats.diastolicBP) && modelCapabilities.vitals.systolicBP && <HealthCard title="Blood Pressure" value={hasData ? `${healthData.systolicBP}/${healthData.diastolicBP}` : '—'} unit={hasData && selectedStats.map && modelCapabilities.vitals.map ? `mmHg · MAP ${healthData.map}` : 'mmHg'} bgColor="rgba(59,130,246,0.15)" textColor="#60a5fa" history={healthHistory.systolicBP} />}
                {selectedStats.respiratoryRate && modelCapabilities.vitals.respiratoryRate && <HealthCard title="Resp. Rate" value={hasData ? healthData.respiratoryRate : '—'} unit="/min" bgColor="rgba(20,184,166,0.15)" textColor="#2dd4bf" history={healthHistory.respiratoryRate} />}
                {selectedStats.temperature && <HealthCard title="Temp" value={hasData ? healthData.temperature : '—'} unit="°C" bgColor="rgba(245,158,11,0.15)" textColor="#fbbf24" history={healthHistory.temperature} />}
                {selectedStats.spo2 && modelCapabilities.vitals.spo2 && <HealthCard title="SpO2" value={hasData ? healthData.spo2 : '—'} unit="%" bgColor="rgba(34,197,94,0.15)" textColor="#4ade80" history={healthHistory.spo2} />}
                {selectedStats.hrv && modelCapabilities.vitals.hrv && <HealthCard title="HRV" value={hasData ? healthData.hrv : '—'} unit="ms" bgColor="rgba(99,102,241,0.15)" textColor="#818cf8" history={healthHistory.hrv} />}
              </Box>
            </Box>
          )}

          <Box sx={cardSx}>
            <Typography sx={cardLabelSx}>Signals</Typography>
            <SignalsPanel isMonitoring={isMonitoring && !mediapipeFailed} ppgSignal={ppgSignal} ecgSignal={ecgSignal} icgSignal={icgSignal} bpSignal={bpSignal} bufferFrames={bufferFrames} showEcg={modelCapabilities.waveforms.ecg} showIcg={modelCapabilities.waveforms.icg} showBp={modelCapabilities.waveforms.bp} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const cardSx = {
  background: 'rgba(10, 28, 60, 0.6)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
  p: { xs: 1.5, md: 2 },
  display: 'flex',
  flexDirection: 'column' as const,
};

const cardLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.35)',
  letterSpacing: '2.5px',
  textTransform: 'uppercase' as const,
  mb: { xs: 1, md: 1.5 },
};

export default VideoMonitorPage;
